import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReducedFeatureData, ReducedSportData, SportFeatureConfiguration } from '@shared/interfaces/feature-configuration.interface';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { FeatureManagementService } from '@shared/services/feature-management/feature-management.service';
import { AppConstants } from '@core/constants';

const ALLOWED_METRICS: Record<string, Record<'name', string>> = {
  lt_1: { name: "Lactate Threshold 1 (LT1)" },
  min_lactate_eq: { name: "Minimum Lactate Equivalent" },
  mmss: { name: "MMSS - maximum metabolic steady state" },
  anaerobic_threshold: { name: "Anaerobic Threshold (MLSS)" },
  max_aerobic: { name: "Maximum Aerobic Power"}
};

const ADDITIONAL_POINTS: Record<string, Record<'name', string>> = {
  vo2_max_power: { name: "VO2 Max Power" },
};

@Component({
  selector: 'app-lactate-profile-curve-config',
  templateUrl: './lactate-profile-curve-config.component.html',
  styleUrls: ['./lactate-profile-curve-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LactateProfileCurveConfigComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  private enabledFeatures: string[] = [];

  @Input() sportConfig: SportFeatureConfiguration;

  @Output() onLactateCurveConfigChanged: EventEmitter<string[]> = new EventEmitter<string[]>();

  public toggleForm!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly featureManagementService: FeatureManagementService,
  ) { }

  ngOnInit(): void {
    this.enabledFeatures = this.featureManagementService.getEnabledFeatures();

    this.initForm();
    this.populateForm();

    this.toggleForm.valueChanges
      .pipe(
        debounceTime(1000), // Wait 1 second after last change
        distinctUntilChanged(), // Only emit if values actually changed
        takeUntil(this.destroy$) // Clean up subscription
      )
      .subscribe((formValues) => {
        this.updateSportConfigAndSave(formValues);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.populateForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    const formControls: { [key: string]: boolean } = {};

    this.allMetricSlugs().forEach((slug) => {
      formControls[slug] = false;
    });

    this.toggleForm = this.fb.group(formControls);
  }

  populateForm(): void {
    const enabledSlugs = this.sportConfig?.data?.lactate_profile_curve || [];
    this.allMetricSlugs().forEach((slug) => {
      const isEnabled = enabledSlugs.includes(slug);
      if (this.toggleForm) {
        this.toggleForm.get(slug)?.setValue(isEnabled, { emitEvent: false });
      }
    });
  }

  allMetrics() {
    const filteredAllowedMetrics = Object.fromEntries(
      Object.entries(ALLOWED_METRICS).filter(([key]) => this.enabledFeatures.includes(key))
    );
    return { ...filteredAllowedMetrics, ...ADDITIONAL_POINTS };
  }

  allMetricSlugs(): string[] {
    return Object.keys(this.allMetrics());
  }

  getMetricName(metricSlug: string): string {
    let metricName = this.allMetrics()[metricSlug]?.name ?? "";

    if (metricSlug === "maximum_aerobic_power") {
      const featureData = this.sportConfig.feature as ReducedFeatureData;
      const sportData = featureData?.sport as ReducedSportData;
      if (sportData != null) {
        metricName = sportData?.simulation_type === AppConstants.SIMULATE_TYPE.SPEED ? "Maximum Aerobic Speed" : metricName;
      }
    }

    return metricName;
  }

  updateSportConfigAndSave(formValues: any): void {
    const enabledSlugs = Object.keys(formValues).filter(slug => formValues[slug]);
    this.onLactateCurveConfigChanged.emit(enabledSlugs);
  }
}
